import React, { useState } from "react";
import { Table, Card, Text, Badge } from "@radix-ui/themes";
import { Button } from "../../ds2/components";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import { Thread, ThreadDetails, apiGetThreadDetails } from "../../services/ThreadService";

import "./ThreadsTable.css";

interface ThreadsTableProps {
  threads: Thread[];
  adminSecret: string;
}

export const ThreadsTable: React.FC<ThreadsTableProps> = ({
  threads,
  adminSecret,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [threadDetails, setThreadDetails] = useState<Record<string, ThreadDetails>>({});
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const formatDate = (dateString: Date | string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const toggleRow = async (threadId: string) => {
    const newExpandedRows = new Set(expandedRows);

    if (newExpandedRows.has(threadId)) {
      newExpandedRows.delete(threadId);
    } else {
      newExpandedRows.add(threadId);
      
      // Fetch thread details if not already loaded
      if (!threadDetails[threadId] && !loadingDetails.has(threadId)) {
        setLoadingDetails(prev => new Set(prev).add(threadId));
        
        try {
          const details = await apiGetThreadDetails(threadId, adminSecret);
          if (details) {
            setThreadDetails(prev => ({ ...prev, [threadId]: details }));
          }
        } catch (error) {
          console.error("Error fetching thread details:", error);
        } finally {
          setLoadingDetails(prev => {
            const newSet = new Set(prev);
            newSet.delete(threadId);
            return newSet;
          });
        }
      }
    }

    setExpandedRows(newExpandedRows);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return "N/A";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getThreadTypeBadge = (threadType: string) => {
    const typeColors: Record<string, any> = {
      PERSONA: "blue",
      EMPLOYEE: "green",
      NONE: "gray",
    };
    
    return <Badge color={typeColors[threadType] || "gray"}>{threadType}</Badge>;
  };

  const renderThreadDetails = (thread: Thread) => {
    const details = threadDetails[thread.id];
    const isLoading = loadingDetails.has(thread.id);

    if (isLoading) {
      return (
        <div className="thread-details-loading">
          Loading thread details...
        </div>
      );
    }

    if (!details) {
      return (
        <div className="thread-details-error">
          Failed to load thread details
        </div>
      );
    }

    return (
      <div className="thread-details">
        <div className="thread-details-header">
          <Text weight="bold">Thread Details</Text>
          <div className="thread-details-actions">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => copyToClipboard(JSON.stringify(details, null, 2))}>
              <CopyIcon /> Copy JSON
            </Button>
          </div>
        </div>
        
        <div className="thread-details-content">
          <div className="thread-detail-section">
            <Text weight="medium">Thread Information</Text>
            <div className="thread-metadata">
              <div>
                <Text size="1" color="gray">Session ID:</Text>
                <Text size="1" style={{ fontFamily: "monospace" }}>{details.sessionId}</Text>
              </div>
              <div>
                <Text size="1" color="gray">Collection:</Text>
                <Text size="1">{details.collection.name}</Text>
              </div>
              {details.personaId && (
                <div>
                  <Text size="1" color="gray">Persona ID:</Text>
                  <Text size="1" style={{ fontFamily: "monospace" }}>{details.personaId}</Text>
                </div>
              )}
              {details.employeeId && (
                <div>
                  <Text size="1" color="gray">Employee ID:</Text>
                  <Text size="1" style={{ fontFamily: "monospace" }}>{details.employeeId}</Text>
                </div>
              )}
            </div>
          </div>
          
          <div className="thread-detail-section">
            <Text weight="medium">Messages ({details.messages?.length || 0})</Text>
            <div className="thread-messages">
              {details.messages?.map((message, index) => (
                <div key={message.id || index} className="thread-message">
                  <div className="thread-message-header">
                    <Badge color={message.type.includes('USER') ? 'blue' : 'green'}>
                      {message.type}
                    </Badge>
                    <Text size="1" color="gray">
                      {formatDate(message.createdAt)}
                    </Text>
                    {message.pinned && (
                      <Badge color="orange" size="1">Pinned</Badge>
                    )}
                  </div>
                  <Text className="thread-message-content">
                    {message.content || "N/A"}
                  </Text>
                  {message.user && (
                    <Text size="1" color="gray">
                      User: {message.user.email}
                    </Text>
                  )}
                  {message.employee && (
                    <Text size="1" color="gray">
                      Employee: {message.employee.email || 'Unknown'}
                    </Text>
                  )}
                  <div className="thread-message-metadata" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {message.classification && (
                      <Text size="1" color="gray" style={{ display: 'block' }}>
                        Classification: {message.classification}
                      </Text>
                    )}
                    {message.feedbackRating !== null && message.feedbackRating !== undefined && (
                      <Text size="1" color="gray" style={{ display: 'block' }}>
                        Feedback Rating: {message.feedbackRating}
                      </Text>
                    )}
                    {message.timeToContent !== null && message.timeToContent !== undefined && (
                      <Text size="1" color="gray" style={{ display: 'block' }}>
                        Time to Content: {message.timeToContent}ms
                      </Text>
                    )}
                    {message.timeToClassification !== null && message.timeToClassification !== undefined && (
                      <Text size="1" color="gray" style={{ display: 'block' }}>
                        Time to Classification: {message.timeToClassification}ms
                      </Text>
                    )}
                  </div>
                </div>
              )) || []}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="threads-table">

      <Card>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Thread ID</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Collection</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Messages</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last Message</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {threads.map((thread) => (
              <React.Fragment key={thread.id}>
                <Table.Row className={expandedRows.has(thread.id) ? "expanded" : ""}>
                  <Table.Cell>
                    <div className="thread-id">
                      <Text size="1" style={{ fontFamily: "monospace" }}>
                        {thread.id.substring(0, 8)}...
                      </Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => copyToClipboard(thread.id)}>
                        <CopyIcon />
                      </Button>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="thread-user">
                      <Text weight="medium">
                        {thread.userEmail || thread.userName || "Unknown"}
                      </Text>
                      <Text size="1" color="gray">
                        Session: {thread.sessionId.substring(0, 8)}...
                      </Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="thread-collection">
                      <Text weight="medium">{thread.collection.name}</Text>
                      <Text size="1" color="gray">
                        {thread.collectionId.substring(0, 8)}...
                      </Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    {getThreadTypeBadge(thread.threadType)}
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1">{formatDate(thread.createdAt)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft">
                      {thread.messageCount || 0}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1">
                      {truncateText(thread.lastMessage || "")}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => toggleRow(thread.id)}>
                      {expandedRows.has(thread.id) ? (
                        <ChevronUpIcon />
                      ) : (
                        <ChevronDownIcon />
                      )}
                    </Button>
                  </Table.Cell>
                </Table.Row>
                {expandedRows.has(thread.id) && (
                  <Table.Row className="thread-details-row">
                    <Table.Cell colSpan={8}>
                      {renderThreadDetails(thread)}
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table.Root>
      </Card>

      {threads.length === 0 && (
        <div className="threads-table-empty">
          <Text>No threads found</Text>
        </div>
      )}
    </div>
  );
};